import React, { memo, useMemo } from 'react';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { Icon } from '@iconify/react';
import maleAvatar from '../../../../img/maleAvatar.png';
import femaleAvatar from '../../../../img/femaleAvatar.png';
import { dayNames } from './utils/constaints';

const TutorCard = memo(function TutorCard({
  tutor,
  onViewProfile,
  onTpHistory,
  onAssignTutor,
  setUpdateSlot,
  /* onUpdateRemarks */
}) {
  const slotTableData = useMemo(() => {
    const availability = tutor?.availability || [];

    const result = [];
    availability.forEach((data) => {
      const slot = data?.slots || {};
      const times = [];

      slot?.forEach((v) => {
        const time = `${v?.from_time} - ${v?.to_time}`;
        times.push(time);
      });

      const day = dayNames[data?.week_day];

      result.push({ day, times: times?.join(', ') });
    });

    return result;
  }, [tutor]);

  const getProfileImage = () => {
    if (tutor?.trainer_image !== 'default.png' && tutor?.trainer_image != null) {
      return process.env.REACT_APP_UPLOADS_URL + tutor?.trainer_image;
    }

    const gender = tutor?.gender?.toLowerCase();

    if (gender === 'female') {
      return femaleAvatar;
    }

    if (gender === 'male') {
      return maleAvatar;
    }

    return maleAvatar;
  };
  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': { boxShadow: '0 12px 24px rgba(0,0,0,0.05)', borderColor: 'primary.light' },
        transition: 'all 0.3s ease',
      }}
    >
      <Grid container>
        {/* Profile and Primary Stats */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{ bgcolor: 'grey.50', p: 3, borderRight: '1px solid', borderColor: 'divider', position: 'relative' }}
        >
          <Stack alignItems="center" spacing={2} overflow={'hidden'}>
            <Avatar
              src={getProfileImage()}
              alt={tutor?.trainer_name}
              sx={{ width: 100, height: 100, border: '4px solid white', boxShadow: 2 }}
            >
              {/*  {tutor.full_name?.[0]} */}
              {!tutor?.profile_image_url && tutor?.full_name?.[0]}
            </Avatar>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight={700}>
                {tutor.trainer_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                {tutor.gender} • {tutor.experience} Yrs Exp
              </Typography>
              <Divider orientation="vertical" flexItem />
              <Typography variant="caption" color="primary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                {tutor?.interval_experience ?? ''} INT-EXP
              </Typography>
              <Chip
                label={tutor.trainer_active_status ? 'Active' : 'Non-Active'}
                size="small"
                variant="outlined"
                color={tutor?.trainer_active_status ? 'success' : 'error'}
                sx={{ height: 20, fontSize: '10px', ml: 1 }}
              />
            </Box>
            <Divider sx={{ width: '100%' }} />
            <Stack spacing={1} width="100%">
              <Box display="flex" justifyContent="space-between" alignItems={'center'}>
                <Typography variant="caption">MOU Status</Typography>
                <Chip
                  label={tutor.mou_payment ? 'paid' : 'unpaid'}
                  size="small"
                  color={tutor.mou_payment ? 'success' : 'error'}
                  variant="soft"
                />
              </Box>
              <Box display="flex" justifyContent="space-between" gap={1}>
                <Typography variant="caption">Qualification</Typography>
                <Typography variant="caption" fontWeight={700}>
                  {`${tutor?.trainer_qualification}`}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption">Assigned / Completed</Typography>
                <Typography variant="caption" fontWeight={700}>
                  {`${tutor?.total_assigned}/${tutor?.total_completed_sessions}`}
                </Typography>
              </Box>
            </Stack>
            {/* <Stack spacing={2}> */}
            <Box alignSelf={'flex-start'}>
              <Stack direction="row" spacing={1} mb={0.5}>
                <Icon icon="solar:notebook-bold-duotone" color="#5C6BC0" />
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  SYLLABUS EXPERTISE
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={500}>
                {Array(tutor.syllabus) ? tutor.syllabus.join(',') : '-'}
              </Typography>
            </Box>
            <Box sx={{ alignSelf: 'flex-start' }}>
              <Stack direction="row" spacing={1} mb={0.5}>
                <Icon icon="solar:global-bold-duotone" color="#5C6BC0" />
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  LANGUAGES
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={500}>
                {Array.isArray(tutor.languages)
                  ? tutor.languages.join(', ')
                  : typeof tutor.languages === 'string'
                    ? tutor.languages.split(',').join(', ')
                    : '-'}
              </Typography>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                height: 50,
                width: 250,
                left: -75,
                top: -7,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                rotate: '-45deg',
                bgcolor: tutor?.response_status ? '#4fd829' : '#c64e59',
              }}
              variant="filled"
              size="small"
            >
              <Typography mr={3} color={'white'} fontWeight={600}>
                {tutor?.response_status ? 'Responded' : 'No Response'}
              </Typography>
            </Box>
          </Stack>
        </Grid>

        {/* Detailed Info */}
        <Grid item xs={12} md={6} sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                    <Icon icon="solar:calendar-bold-duotone" color="#5C6BC0" />
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      AVAILABILITY
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 1 }}>
                    {slotTableData.length ? (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        {slotTableData.map((row, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              justifyContent: 'space-between',
                              px: 2,
                              py: 1,
                              gap: 1,
                              borderBottom: index !== slotTableData.length - 1 ? '1px solid' : 'none',
                              borderColor: 'divider',
                              bgcolor: index % 2 === 0 ? 'grey.50' : 'transparent',
                            }}
                          >
                            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 100 }}>
                              {row?.day}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ mt: { xs: 0.5, sm: 0 } }}>
                              {row?.times}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not specified
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" width={'100%'}>
                <Box display={'flex'} gap={1} flex={1} sx={{ flexWrap: 'wrap' }}>
                  <Button variant="soft" size="small" color="primary" onClick={() => onViewProfile(tutor)}>
                    View Profile
                  </Button>
                  <Button variant="soft" size="small" color="info" onClick={() => onTpHistory(tutor)}>
                    TP History
                  </Button>
                  {/*   <Button variant="soft" size="small" color="secondary" onClick={() => onUpdateRemarks(tutor)}>
                    Update Remarks
                  </Button> */}
                  <Button variant="soft" size="small" color="secondary" onClick={() => setUpdateSlot(tutor)}>
                    Update Slot
                  </Button>
                </Box>
                <Stack direction={'row'} sx={{ alignItems: 'center', justifyContent: 'flex-end' }} gap={1}>
                  <Tooltip title="WiFi Available">
                    <Icon
                      icon={tutor?.wifi ? 'fontisto:wifi' : 'ic:twotone-wifi-off'}
                      color={tutor?.wifi ? '#4CAF50' : '#F44336'}
                      width={18}
                    />
                  </Tooltip>
                  <Divider orientation="vertical" variant="fullWidth" flexItem />
                  <Tooltip title="Laptop Available">
                    <Icon
                      icon={tutor?.laptop ? 'ic:baseline-laptop' : 'mdi:laptop-off'}
                      color={tutor?.laptop ? '#4CAF50' : '#F44336'}
                      width={18}
                    />
                  </Tooltip>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Grid>

        {/* Quick Actions & Video */}
        <Grid item xs={12} md={3} sx={{ p: 3, bgcolor: 'primary.lighter', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Demo Session
          </Typography>
          <Box
            onClick={() => tutor?.demo_video_link && window.open(tutor?.demo_video_link, '_blank')}
            sx={{
              flex: 1,
              bgcolor: tutor?.demo_video_link ? 'common.white' : '#c9c6c6ff',
              borderRadius: 2,
              display: 'flex',
              position: 'relative',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: tutor.demo_video_link ? 'pointer' : 'default',
              border: '1px solid',
              borderColor: 'primary.light',
              mb: 2,
              '&:hover': tutor.demo_video_link
                ? { bgcolor: '#d6d5f0', color: 'black', '& svg': { color: 'white' } }
                : {},
              transition: '0.2s',
            }}
          >
            <Icon icon="solar:play-circle-bold-duotone" width={40} color="#5C6BC0" />
            <Typography variant="caption" fontWeight={600} mt={1}>
              Watch Demo
            </Typography>
            <IconButton
              color="success"
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
              }}
              disabled={!Boolean(tutor?.demo_video_link)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const cleanNumber = tutor?.mobile?.replace(/\D/g, ''); // Removes +, spaces, and dashes
                const url = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(tutor?.demo_video_link)}`;

                window.open(url, '_blank');
              }}
            >
              <WhatsAppIcon
                color="inherti"
                sx={{
                  width: 20,
                  height: 20,
                }}
              />
            </IconButton>
          </Box>

          <Button
            disabled={
              !Boolean(
                tutor?.mou_payment &&
                tutor?.trainer_active_status &&
                tutor?.trainer_completion_status &&
                Boolean(tutor?.trainer_bank_details_data),
              )
            }
            fullWidth
            variant="contained"
            size="large"
            sx={{ borderRadius: 2, py: 1.5, fontWeight: 700 }}
            onClick={() => onAssignTutor(tutor)}
          >
            Assign This Tutor
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
});

export default TutorCard;
